export type FieldMapping = {
  column: string;
  apiField: string;
  type: string;
  required: boolean;
  description: string;
  example: string;
  extraInfo?: string[];
};

export const GUARDIAN_ADDRESS_INFO: string[] = [
  'import.guardianAddressInfo.requiredForGuardian1',
  'import.guardianAddressInfo.usedForTravelSearch',
  'import.guardianAddressInfo.busStopOptional',
];

export const DELIVERY_ADDRESS_INFO: string[] = [
  'import.deliveryAddressInfo.cardDelivery',
];

export const EMAIL_DESCRIPTION =
  'E-post för utskick samt inloggning i självbetjäningen';

const student = [
  {
    column: 'Förnamn',
    apiField: 'first_name',
    type: 'Fritext',
    required: true,
    description: 'Personens förnamn',
    example: 'Anna',
  },
  {
    column: 'Efternamn',
    apiField: 'last_name',
    type: 'Fritext',
    required: true,
    description: 'Personens efternamn',
    example: 'Andersson',
  },
  {
    column: 'Personnummer',
    apiField: 'national_identity_number',
    type: 'Person-/TF-nummer',
    required: true,
    description:
      'Personens fulla personnummer eller samordningsnummer. 10-siffriga personnummer (ÅÅMMDD-XXXX) omvandlas automatiskt till 12 siffror (ÅÅÅÅMMDD-XXXX).',
    example: '20100101-1234',
  },
  {
    column: 'E-post',
    apiField: 'email',
    type: 'E-post',
    required: false,
    description: EMAIL_DESCRIPTION,
    example: 'anna@example.com',
  },
];

const guardian1Email = [
  {
    column: 'Vårdnadshavare 1: E-post',
    apiField: 'guardian1_email',
    type: 'E-post',
    required: false,
    description: EMAIL_DESCRIPTION,
    example: 'anna.andersson@example.com',
  },
];

const guardian1Address: FieldMapping[] = [
  {
    column: 'Vårdnadshavare 1: c/o',
    apiField: 'guardian1_address.care_of',
    type: 'Fritext',
    required: false,
    description: 'c/o-namn för vårdnadshavare 1, utan "c/o"',
    example: 'Anna Andersson',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 1: Adress',
    apiField: 'guardian1_address.street',
    type: 'Fritext',
    required: false,
    description: 'Adress för vårdnadshavare 1',
    example: 'Storgatan 1',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 1: Postnummer',
    apiField: 'guardian1_address.zip_code',
    type: 'Postnummer',
    required: false,
    description: 'Postnummer för vårdnadshavare 1',
    example: '12345',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 1: Postort',
    apiField: 'guardian1_address.city',
    type: 'Fritext',
    required: false,
    description: 'Postort för vårdnadshavare 1',
    example: 'Stockholm',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 1: Hållplats',
    apiField: 'guardian1_bus_stop',
    type: 'Fritext',
    required: false,
    description:
      'Hållplats för vårdnadshavare 1. Även om adress skulle vara angivet så kommer biljettköp misslyckas om hållplatsen inte finns.',
    example: 'Exempelhållplatsen 1',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
];

const guardian2Email = [
  {
    column: 'Vårdnadshavare 2: E-post',
    apiField: 'guardian2_email',
    type: 'E-post',
    required: false,
    description: EMAIL_DESCRIPTION,
    example: 'berit.berntsson@example.com',
  },
];

const guardian2Address: FieldMapping[] = [
  {
    column: 'Vårdnadshavare 2: c/o',
    apiField: 'guardian2_address.care_of',
    type: 'Fritext',
    required: false,
    description: 'c/o-namn för vårdnadshavare 2, utan "c/o"',
    example: 'Berit Berntsson',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 2: Adress',
    apiField: 'guardian2_address.street',
    type: 'Fritext',
    required: false,
    description: 'Adress för vårdnadshavare 2',
    example: 'Storgatan 2',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 2: Postnummer',
    apiField: 'guardian2_address.zip_code',
    type: 'Postnummer',
    required: false,
    description: 'Postnummer för vårdnadshavare 2',
    example: '12346',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 2: Postort',
    apiField: 'guardian2_address.city',
    type: 'Fritext',
    required: false,
    description: 'Postort för vårdnadshavare 2',
    example: 'Stockholm',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
  {
    column: 'Vårdnadshavare 2: Hållplats',
    apiField: 'guardian2_bus_stop',
    type: 'Fritext',
    required: false,
    description:
      'Hållplats för vårdnadshavare 2. Även om adress skulle vara angivet så kommer biljettköp misslyckas om hållplatsen inte finns.',
    example: 'Exempelhållplatsen 2',
    extraInfo: GUARDIAN_ADDRESS_INFO,
  },
];

const school = [
  {
    column: 'Skola',
    apiField: 'school_name',
    type: 'Fritext',
    required: false,
    description: 'Namn på skolan som eleven tillhör',
    example: 'Centralskolan',
  },
  {
    column: 'Skolans adress',
    apiField: 'school_address.street',
    type: 'Fritext',
    required: true,
    description: 'Adress för skolan',
    example: 'Skolvägen 10',
  },
  {
    column: 'Skolans postnummer',
    apiField: 'school_address.zip_code',
    type: 'Postnummer',
    required: true,
    description: 'Postnummer för skolan',
    example: '12346',
  },
  {
    column: 'Skolans postort',
    apiField: 'school_address.city',
    type: 'Fritext',
    required: true,
    description: 'Postorten för skolan',
    example: 'Stockholm',
  },
  {
    column: 'Skolans hållplats',
    apiField: 'school_bus_stop',
    type: 'Fritext',
    required: false,
    description:
      'Hållplats för skolan. Även om adress skulle vara angivet så kommer biljettköp misslyckas om hållplatsen inte finns.',
    example: 'Skolhållplatsen',
  },
];

const delivery: FieldMapping[] = [
  {
    column: 'Leverans: c/o',
    apiField: 'delivery_address.care_of',
    type: 'Fritext',
    required: false,
    description: 'c/o-namn för leverans, utan "c/o"',
    example: 'Anna Andersson',
    extraInfo: DELIVERY_ADDRESS_INFO,
  },
  {
    column: 'Leverans: Adress',
    apiField: 'delivery_address.street',
    type: 'Fritext',
    required: true,
    description: 'Adress för leverans',
    example: 'Storgatan 1',
    extraInfo: DELIVERY_ADDRESS_INFO,
  },
  {
    column: 'Leverans: Postnummer',
    apiField: 'delivery_address.zip_code',
    type: 'Postnummer',
    required: true,
    description: 'Postnummer för leverans',
    example: '12345',
    extraInfo: DELIVERY_ADDRESS_INFO,
  },
  {
    column: 'Leverans: Postort',
    apiField: 'delivery_address.city',
    type: 'Fritext',
    required: true,
    description: 'Postort för leverans',
    example: 'Stockholm',
    extraInfo: DELIVERY_ADDRESS_INFO,
  },
];

const halfProduct: FieldMapping[] = [
  {
    column: 'Halv produkt',
    apiField: 'half_product',
    type: '"Ja"/"Nej"/Tomt',
    required: false,
    description: 'Om produkten ska rabatteras enligt separat överenskommelse',
    example: 'Ja',
  },
];

const childAndYouthFieldMappingsAll: FieldMapping[] = [
  ...student,
  ...guardian1Email,
  ...guardian2Email,
  ...delivery,
  ...halfProduct,
];

const studentTravelFieldMappingsAll: FieldMapping[] = [
  ...student,
  ...guardian1Email,
  ...guardian1Address,
  ...guardian2Email,
  ...guardian2Address,
  ...school,
  ...delivery,
  ...halfProduct,
];

export const getChildAndYouthFieldMappings = (
  disableHalfProduct: boolean,
): FieldMapping[] =>
  disableHalfProduct
    ? childAndYouthFieldMappingsAll.filter((f) => f.apiField !== 'half_product')
    : childAndYouthFieldMappingsAll;

export const getStudentTravelFieldMappings = (
  disableHalfProduct: boolean,
): FieldMapping[] =>
  disableHalfProduct
    ? studentTravelFieldMappingsAll.filter((f) => f.apiField !== 'half_product')
    : studentTravelFieldMappingsAll;

export const childAndYouthFieldMappings = getChildAndYouthFieldMappings(false);

export const studentTravelFieldMappings = getStudentTravelFieldMappings(false);

export const studentTravelFields = studentTravelFieldMappings.map(
  (f) => f.apiField,
);
export const childAndYouthFields = childAndYouthFieldMappings.map(
  (f) => f.apiField,
);
